import { mapValues } from "lodash-es";
import { computed, reactive } from "vue";

import type { Action, ActionData } from "@/action/types";
import type { ClickableEmits, ClickableProps } from "@/composables/Props";
import { useGlobalI18n } from "@/translations/i18n";
import { injectStrict } from "@/utils/vue";

import { actionSourceKey } from "./injectKeys";

const { t } = useGlobalI18n();

// style must NOT be used for component props
type ActionAttrs = ClickableProps & { style?: Record<"display", "none"> } & ClickableEmits;

export function action<TParams extends unknown[], TReturn>(
  action: Action<TParams, TReturn>,
  options?: { click?: () => void },
  ...params: TParams
): ActionAttrs {
  const name = action.data.name;
  const text = name ? t(name) : "";
  const label = action.data.label;
  const actionSource = injectStrict(actionSourceKey);
  const attrs = {
    icon: action.data.icon,
    text,
    shortcut: action.data.shortcut.name(),
    ariaLabel: label ? t(label) : text,
    onClick: () => {
      action(actionSource, ...params);
      options?.click?.();
    },
  };
  return action.data.active || action.data.disabled ? reactiveAttrs(attrs, action.data) : attrs;
}

function reactiveAttrs(attrs: ActionAttrs, data: ActionData<never[], unknown>) {
  // calling reactive unwraps the computed values so the types are correct
  if (data.active) {
    attrs.activated = computed(() => data.active?.()) as never;
  }
  if (data.disabled) {
    attrs.style = computed(() => (data.disabled?.() ? { display: "none" } : undefined)) as never;
  }
  return reactive(attrs);
}

type ActionMap<T extends string> = Record<T, Action<never[], unknown>>;
type ActionAttrMap<T extends string> = Record<T, ActionAttrs>;

export function actionMap<K extends string>(actions: ActionMap<K>): ActionAttrMap<K> {
  return mapValues(actions, (a) => action(a));
}

export function actionOptMap<K extends string>(
  actions: Partial<ActionMap<K>> | undefined,
): Partial<ActionAttrMap<K>> {
  return mapValues(actions || {}, (a) => (a ? action(a) : undefined));
}
